<template>
  <div class="player-metrics">
    <div class="header-row">
      <h2 class="mr-2">Device Metrics</h2>
      <span v-if="isLoading" class="inline-spinner" />
      <span v-else class="timestamp" :title="tooltipText">{{ lastUpdatedText }}</span>
    </div>

    <div v-if="error" class="error-text">{{ error }}</div>

    <div v-if="metrics" class="metrics-grid">
      <!-- Memory -->
      <div class="metric">
        <div class="metric-header">
          <div class="metric-title-wrap">
            <span class="metric-title">Memory</span>
            <span v-if="memoryStatus !== 'good'" class="status-icon" :class="{ warn: memoryStatus==='warn', bad: memoryStatus==='bad' }" :title="memoryStatus==='warn' ? 'Warning' : 'Critical'">⚠</span>
          </div>
          <span class="metric-value">{{ memUsedMb }} / {{ metrics.mem_total_mb }} MB</span>
        </div>
        <div class="bar">
          <div class="bar-fill mem" :class="{ grad: memoryStatus === 'warn' }" :style="{ width: memUsagePct + '%' }" />
        </div>
        <div class="bar-meta">{{ memUsagePct.toFixed(0) }}%</div>
      </div>

      <!-- CPU -->
      <div class="metric">
        <div class="metric-header">
          <div class="metric-title-wrap">
            <span class="metric-title">CPU</span>
            <span v-if="cpuStatus !== 'good'" class="status-icon" :class="{ warn: cpuStatus==='warn', bad: cpuStatus==='bad' }" :title="cpuStatus==='warn' ? 'Warning' : 'Critical'">⚠</span>
          </div>
          <span class="metric-value">{{ metrics.cpu_usage_pct.toFixed(1) }}%</span>
        </div>
        <div class="bar">
          <div class="bar-fill cpu" :class="{ grad: cpuStatus === 'warn' }" :style="{ width: cpuPct + '%' }" />
        </div>
        <div class="bar-meta">{{ clampPct(metrics.cpu_usage_pct).toFixed(0) }}%</div>
      </div>

      <!-- Disk -->
      <div class="metric">
        <div class="metric-header">
          <div class="metric-title-wrap">
            <span class="metric-title">Disk</span>
            <span v-if="diskStatus !== 'good'" class="status-icon" :class="{ warn: diskStatus==='warn', bad: diskStatus==='bad' }" :title="diskStatus==='warn' ? 'Warning' : 'Critical'">⚠</span>
          </div>
          <span class="metric-value">{{ diskUsedMb }} / {{ metrics.disk_total_mb }} MB</span>
        </div>
        <div class="bar">
          <div class="bar-fill disk" :class="{ grad: diskStatus === 'warn' }" :style="{ width: diskUsagePct + '%' }" />
        </div>
        <div class="bar-meta">{{ diskUsagePct.toFixed(0) }}%</div>
      </div>

      <!-- Temperature -->
      <div class="metric">
        <div class="metric-header">
          <div class="metric-title-wrap">
            <span class="metric-title">Temperature</span>
            <span v-if="tempStatus !== 'good'" class="status-icon" :class="{ warn: tempStatus==='warn', bad: tempStatus==='bad' }" :title="tempStatus==='warn' ? 'Warning' : 'Critical'">⚠</span>
          </div>
          <span class="metric-value">{{ metrics.temperature_c.toFixed(1) }}°C</span>
        </div>
        <div class="bar">
          <div class="bar-fill temp" :class="{ grad: tempStatus === 'warn' }" :style="{ width: tempPct + '%' }" />
        </div>
        <div class="bar-meta">{{ tempPct.toFixed(0) }}%</div>
      </div>
    </div>

    <div v-else-if="!isLoading && !error" class="no-data">No recent metrics</div>

    <div class="now-playing">
      <label>Currently Playing</label>
      <div v-if="nowPlayingList.length" class="now-playing-thumbs">
        <div class="np-item" v-for="name in nowPlayingList" :key="name">
          <img v-if="nowPlayingThumbMap[name]" :src="nowPlayingThumbMap[name]" class="np-thumb" :alt="name" />
          <div class="np-name">{{ name }}</div>
        </div>
      </div>
      <div v-else class="now-playing-value">—</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
// @ts-nocheck
import { onMounted, onUnmounted, ref, computed, watch } from 'vue';
import { getLatestMetricsByPlayerUuid, type MetricsData } from '@/helpers/api/metrics';
import useApi from '@/helpers/api/api';

const props = defineProps<{ deviceUuid?: string|null; pollMs?: number }>();

const metrics = ref<MetricsData | null>(null);
const isLoading = ref(false);
const error = ref('');
const lastUpdated = ref<Date | null>(null);
let timer: number | null = null;

function addDashesToUuid(uuid?: string|null): string | null {
  if (!uuid) return null;
  const value = uuid.replace(/-/g, '');
  if (!/^[0-9a-fA-F]{32}$/.test(value)) return null;
  return `${value.substring(0,8)}-${value.substring(8,12)}-${value.substring(12,16)}-${value.substring(16,20)}-${value.substring(20)}`.toLowerCase();
}

async function fetchLatest() {
  const playerUuid = addDashesToUuid(props.deviceUuid);
  if (!playerUuid) {
    error.value = 'Device UUID not available';
    return;
  }
  isLoading.value = true;
  error.value = '';
  try {
    const data = await getLatestMetricsByPlayerUuid(playerUuid, 1);
    metrics.value = data && data.length > 0 ? data[0] : null;
    lastUpdated.value = metrics.value ? new Date(metrics.value.created_at) : null;
    // Load thumbnails for currently playing
    const currentlyPlaying = metrics.value?.currently_playing || [];
    if (Array.isArray(currentlyPlaying) && currentlyPlaying.length > 0) {
      // Build items with name and url (if available)
      const items = currentlyPlaying.map(item => {
        if (typeof item === 'string') {
          return { name: item, url: '' };
        }
        return { name: item?.contentTitle || item?.title || '', url: item?.contentUrl || '' };
      }).filter(i => i.name);
      loadThumbnailsForNowPlaying(items);
    } else {
      nowPlayingThumbMap.value = {};
    }
  } catch (e: any) {
    error.value = e?.message || 'Failed to load metrics';
  } finally {
    isLoading.value = false;
  }
}

function startPolling() {
  stopPolling();
  const interval = props.pollMs ?? 10000;
  timer = window.setInterval(fetchLatest, interval);
}

function stopPolling() {
  if (timer) {
    window.clearInterval(timer);
    timer = null;
  }
}

onMounted(() => {
  fetchLatest();
  startPolling();
});

onUnmounted(() => {
  stopPolling();
});

watch(() => props.deviceUuid, () => {
  fetchLatest();
});

const memUsedMb = computed(() => metrics.value ? Math.max(metrics.value.mem_total_mb - metrics.value.mem_free_mb, 0) : 0);
const memUsagePct = computed(() => metrics.value && metrics.value.mem_total_mb > 0
  ? (memUsedMb.value / metrics.value.mem_total_mb) * 100
  : 0);

const diskUsedMb = computed(() => metrics.value ? Math.max(metrics.value.disk_total_mb - metrics.value.disk_free_mb, 0) : 0);
const diskUsagePct = computed(() => metrics.value && metrics.value.disk_total_mb > 0
  ? (diskUsedMb.value / metrics.value.disk_total_mb) * 100
  : 0);

function clampPct(value) {
  return Math.max(0, Math.min(100, value ?? 0));
}

// Helpers for thumbnail matching (declared at script root for linting)
function getBaseName(input: string): string {
  if (!input) return '';
  const parts = input.split(/[\\/]/g); // handle paths
  return parts[parts.length - 1];
}
function stripExtension(input: string): string {
  if (!input) return '';
  const idx = input.lastIndexOf('.');
  return idx > 0 ? input.substring(0, idx) : input;
}
function normalizeForCompare(input: string): string {
  return (input || '')
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[_-]+/g, '')
    .trim();
}

// CPU percent clamped for consistent use in template
const cpuPct = computed(() => clampPct(metrics.value?.cpu_usage_pct));

// Determine status based on thresholds (good < warn, warn < bad, else bad)
function getStatusForPercent(value, thresholds) {
  const v = clampPct(value ?? 0);
  if (v < thresholds.warn) return 'good';
  if (v < thresholds.bad) return 'warn';
  return 'bad';
}

// Reasonable defaults; adjust as needed
const memoryStatus = computed(() => getStatusForPercent(memUsagePct.value, { warn: 75, bad: 85 }));
const cpuStatus = computed(() => getStatusForPercent(cpuPct.value, { warn: 70, bad: 90 }));
const diskStatus = computed(() => getStatusForPercent(diskUsagePct.value, { warn: 70, bad: 90 }));
// For temperature, compare actual Celsius (assume 100°C as max scale) with thresholds
const tempStatus = computed(() => {
  const tempC = metrics.value?.temperature_c ?? 0;
  if (tempC < 70) return 'good';
  if (tempC < 85) return 'warn';
  return 'bad';
});

const tempPct = computed(() => {
  // Normalize temperature to 0-100% assuming 100°C as max scale
  const temp = metrics.value?.temperature_c ?? 0;
  return clampPct((temp / 100) * 100);
});

const nowPlayingList = computed<string[]>(() => {
  const list = metrics.value?.currently_playing || [];
  if (!Array.isArray(list)) return [];
  // Names for display
  return list.map(item => typeof item === 'string' ? item : item.contentTitle || item.title || '')
    .filter(Boolean);
});

const nowPlayingItems = computed<{ name: string; url: string }[]>(() => {
  const list = metrics.value?.currently_playing || [];
  if (!Array.isArray(list)) return [];
  return list.map(item => {
    if (typeof item === 'string') return { name: item, url: '' };
    return { name: item?.contentTitle || item?.title || '', url: item?.contentUrl || '' };
  }).filter(i => i.name);
});

const nowPlayingThumbMap = ref<Record<string, string>>({});

function extractUuidFromUrl(url: string): string | null {
  if (!url) return null;
  try {
    const withoutQuery = url.split('?')[0] || url;
    const parts = withoutQuery.split('/');
    const last = parts[parts.length - 1] || '';
    const base = stripExtension(last);
    // GUID format xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
    const guidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    return guidRegex.test(base) ? base : null;
  } catch (_) {
    return null;
  }
}

async function searchAssetsByPublicName(publicName: string): Promise<any[]> {
  try {
    const { post } = useApi();
    const listRequestOptions = {
      limit: 10,
      offset: 0,
      query: { fieldNames: ['PublicName'], term: publicName },
      filters: [],
      sorts: [{ fieldName: 'DateLastModified', isDescending: true }]
    };
    const { responseBody } = await post('assetsearch', listRequestOptions, { isLogoutOnInvalidAuth: true });
    return responseBody?.results || [];
  } catch (e) {
    console.error('[PlayerMetrics] searchAssetsByPublicName failed for publicName:', publicName, e);
    return [];
  }
}

async function loadThumbnailsForNowPlaying(items: { name: string; url: string }[]) {
  try {
    if (!items || items.length === 0) return;
    const uniqueItems = [] as { name: string; url: string }[];
    const seen = new Set<string>();
    for (const it of items) {
      if (it?.name && !seen.has(it.name)) { uniqueItems.push(it); seen.add(it.name); }
    }

    const fetchCandidatesByName = async (term: string) => {
      try {
        const results = await searchAssetsByName(term);
        return results || [];
      } catch (e) {
        return [] as any[];
      }
    };

    const thumbMap: Record<string, string> = {};
    
    for (const { name: originalName, url } of uniqueItems) {
      const base = getBaseName(originalName);
      const nameNoExt = stripExtension(base);
      const normOriginal = normalizeForCompare(originalName);
      const normBase = normalizeForCompare(base);
      const normNoExt = normalizeForCompare(nameNoExt);

      // Prefer lookup by UUID from contentUrl when available
      let candidates: any[] = [];
      const uuid = extractUuidFromUrl(url);
      if (uuid) {
        candidates = await searchAssetsByPublicName(uuid);
      }
      // Fallbacks by name when UUID not found or no results
      if (!candidates.length) {
        candidates = await fetchCandidatesByName(base);
      }
      if (!candidates.length && nameNoExt) {
        candidates = await fetchCandidatesByName(nameNoExt);
      }
      if (!candidates.length) {
        candidates = await fetchCandidatesByName(originalName);
      }

      console.debug('[PlayerMetrics] NowPlaying lookup', { originalName, base, nameNoExt, candidatesCount: candidates.length });

      let picked: any = null;
      let bestScore = -1;
      for (const item of candidates) {
        const itemName: string = item?.name || item?.Name || '';
        const normItem = normalizeForCompare(itemName);
        let score = 0;
        if (normItem === normOriginal) score = 100;
        else if (normItem === normBase) score = 95;
        else if (normItem === normNoExt) score = 90;
        else if (normItem.includes(normNoExt) && normNoExt.length > 0) score = 70;
        else if (normItem.includes(normBase) && normBase.length > 0) score = 60;
        if (score > bestScore) { bestScore = score; picked = item; }
      }

      // Try preview fields first
      let previewUri = picked?.previewUri
        || picked?.versions?.[0]?.resource?.publicUrl
        || picked?.Versions?.[0]?.PublicUrl
        || null;

      // AssetLinks fallback: try picked id, then try all candidates until one works
      if (!previewUri) {
        const tryIds: Array<number|string> = [];
        const pickedId = picked?.id ?? picked?.Id ?? picked?.assetId ?? picked?.AssetId;
        if (pickedId != null) tryIds.push(pickedId);
        for (const c of candidates) {
          const cid = c?.id ?? c?.Id ?? c?.assetId ?? c?.AssetId;
          if (cid != null && !tryIds.includes(cid)) tryIds.push(cid);
        }
        for (const aid of tryIds) {
          const link = await getAssetLinksPreviewUrl(aid);
          if (link) { previewUri = link; break; }
        }
      }

      if (previewUri) {
        thumbMap[originalName] = previewUri;
      } else {
        console.warn('[PlayerMetrics] No thumbnail found for', originalName);
      }
    }
    nowPlayingThumbMap.value = thumbMap;
  } catch (_) {
    void 0;
  }
}

watch(nowPlayingItems, (items) => {
  loadThumbnailsForNowPlaying(items);
});

const lastUpdatedText = computed(() => {
  if (!lastUpdated.value) return '';
  return `Updated ${lastUpdated.value.toLocaleTimeString()}`;
});

const tooltipText = computed(() => lastUpdated.value ? lastUpdated.value.toLocaleString() : '');

async function getAssetLinksPreviewUrl(assetId: number|string): Promise<string|null> {
  try {
    const { post } = useApi();
    const path = `api/assets/${assetId}/AssetLinks`;
    // Try active data source first
    let result = await post(path, {}, { isLogoutOnInvalidAuth: true });
    let preview = result?.responseBody?.previewUrl || result?.responseBody?.previewUri || null;
    if (preview) return preview;
    // Fallback to environment config base URL
    result = await post(path, {}, { isLogoutOnInvalidAuth: true });
    preview = result?.responseBody?.previewUrl || result?.responseBody?.previewUri || null;
    if (!preview) {
      console.warn('[PlayerMetrics] AssetLinks returned no previewUrl for assetId (both bases):', assetId, result?.responseBody);
    }
    return preview;
  } catch (e) {
    console.error('[PlayerMetrics] AssetLinks request failed', assetId, e);
    return null;
  }
}

async function searchAssetsByName(term: string): Promise<any[]> {
 try {
   const { post } = useApi();
   const listRequestOptions = {
     limit: 10,
      offset: 0,
      query: { fieldNames: ['Name'], term },
      filters: [],
      sorts: [{ fieldName: 'DateLastModified', isDescending: true }]
    };
    // Use default baseUrl from store (handles relative paths correctly)
    const { responseBody } = await post('assetsearch', listRequestOptions, { isLogoutOnInvalidAuth: true });
    return responseBody?.results || [];
  } catch (e) {
    console.error('[PlayerMetrics] searchAssetsByName failed for term:', term, e);
    return [];
  }
}
</script>

<style scoped>
.player-metrics {
  margin-top: 1rem;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.timestamp {
  color: #6b7280;
  font-size: 0.8rem;
}

.inline-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-text {
  color: #b91c1c;
  margin: 0.5rem 0;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
}

.metric {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
}

.metric-title-wrap {
  display: inline-flex;
  align-items: center;
}

.metric-title {
  font-weight: 600;
  color: #374151;
}

.metric-value {
  color: #374151;
  font-size: 0.85rem;
}

.bar {
  height: 10px;
  background: #f3f4f6;
  border-radius: 9999px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  transition: width 0.3s ease;
}

/* Per-metric gradients */
.bar-fill.mem { background: #00FFFF; }
.bar-fill.mem.grad { background: linear-gradient(90deg, #00FFFF 0%, #69E7FF 100%); }
.bar-fill.cpu { background: #BAFF5C; }
.bar-fill.cpu.grad { background: linear-gradient(90deg, #BAFF5C 0%, #00CF67 100%); }
.bar-fill.disk { background: #6D76FF; }
.bar-fill.disk.grad { background: linear-gradient(90deg, #6D76FF 0%, #0C00AD 100%); }
.bar-fill.temp { background: #6B1AB1; }
.bar-fill.temp.grad { background: linear-gradient(90deg, #6B1AB1 0%, #A420C8 100%); }

/* Status icons */
.status-icon {
  margin-left: 0.25rem;
  font-size: 1rem;
  line-height: 1;
}
.status-icon.warn { color: #F59E0B; }
.status-icon.bad { color: #DC2626; }

.bar-meta {
  color: #6b7280;
  font-size: 0.8rem;
}

.now-playing {
  margin-top: 0.75rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.now-playing label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin: 0.2rem 0.25rem 0 0;
}

.now-playing-value {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.now-playing-thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.np-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
}

.np-thumb {
  width: 256px;
  height: 144px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.np-name {
  font-size: 0.9rem;
  color: #374151;
  text-align: center;
  line-height: 1.25;
  max-width: 256px;
  width: 256px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.no-data {
  color: #6b7280;
  font-style: italic;
}
</style>

